import type { PlayerItemVisitResponse } from '@on3/api';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { On3Avatar } from 'components/Image/On3Avatar';
import { StatusContainer } from 'components/RecruitmentStatus/StatusContainer';
import { UndecidedStatus } from 'components/RecruitmentStatus/UndecidedStatus';
import { AthleteVerified } from 'components/UI/AthleteVerified/AthleteVerified';
import { TextLink } from 'components/UI/Link/TextLink';
import {
  ConditionalLink,
  StarRating,
} from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { stringifyQuery } from 'utils/stringifyQuery';

import styles from './PlayerCard.module.scss';

export const PlayerCard = ({
  player,
  rating,
  status,
  rpm,
  nilValue,
  official,
}: PlayerItemVisitResponse) => {
  const {
    fullName,
    slug,
    hometown,
    state,
    defaultAsset,
    height,
    weight,
    athleteVerified,
    highSchool,
    position,
  } = player || {};

  const {
    query: { sport, year },
  } = useRouter();
  const sportQuery = stringifyQuery(sport).toLowerCase();
  const hasPredictions = rpm ? rpm.length > 0 : false;
  const hasStatus =
    status && status.type !== 'None' && status.committedAsset?.slug;

  const showNil = !!nilValue?.totalValue && nilValue.totalValue >= 5000;

  return (
    <div className={styles.cardWrapper}>
      <div className={styles.playerName}>
        <TextLink color="primary" href={`/db/${slug}/`} variant="h5">
          {fullName}
        </TextLink>
        {athleteVerified && <AthleteVerified />}
      </div>
      <div className={styles.playerAvatar}>
        <On3Avatar
          altText={`${fullName} Avatar`}
          className={styles.avatar}
          domain={defaultAsset?.domain}
          source={defaultAsset?.source}
        />
        {official && (
          <Text className={styles.official} variant="subtitle1">
            Official
          </Text>
        )}
        {showNil && nilValue.totalValue && (
          <div className={styles.nilContainer}>
            <Text className={styles.nilLabel}>NIL Value:</Text>
            <Text className={styles.nilValuation}>
              {formatCurrency(nilValue.totalValue)}
            </Text>
          </div>
        )}
      </div>
      <div className={styles.playerInfoContainer}>
        <div className={styles.infoLine1}>
          <Text className={styles.info}>
            {position?.abbr} / {height} / {weight}
          </Text>
        </div>
        <div className={styles.infoLine2}>
          <TextLink
            className={styles.highschool}
            href={`/high-school/${highSchool?.urlSlug}/`}
          >
            {highSchool?.name}
          </TextLink>
          <Text className={styles.hometown}>
            ({hometown?.name}, {state?.abbr})
          </Text>
        </div>
      </div>
      <div className={styles.stars}>
        <StarRating
          href={`/db/${slug}/industry-comparison/`}
          large
          stars={rating?.consensusStars}
        />
        <TextLink
          className={styles.ratingMobile}
          href={`/db/${slug}/industry-comparison/`}
          underline="none"
          variant="subtitle1"
        >
          {rating?.consensusRating?.toFixed(2)}
        </TextLink>
      </div>
      <TextLink
        className={styles.rating}
        href={`/db/${slug}/industry-comparison/`}
        underline="none"
        variant="subtitle1"
      >
        {rating?.consensusRating?.toFixed(2)}
      </TextLink>
      <div className={styles.rankingContainer}>
        <div className={styles.rankType}>
          <span aria-label="national rank">NATL</span>
          <ConditionalLink
            className={styles.ranking}
            condition={!!(sport && year)}
            href={`/db/rankings/industry-player/${sportQuery}`}
          >
            <Text className={styles.ranking} color="primary">
              {rating?.consensusNationalRank || '-'}
            </Text>
          </ConditionalLink>
        </div>
        <div className={styles.rankType}>
          <span aria-label="position rank">POS</span>
          <ConditionalLink
            className={styles.ranking}
            condition={!!(rating?.positionAbbr && sport && year)}
            href={`/db/rankings/industry-player/${sportQuery}/${year}/?position=${rating?.positionAbbr?.toLowerCase()}`}
          >
            <Text className={styles.ranking} color="primary">
              {rating?.consensusPositionRank || '-'}
            </Text>
          </ConditionalLink>
        </div>
        <div className={styles.rankType}>
          <span aria-label="state rank">ST</span>
          <ConditionalLink
            className={styles.ranking}
            condition={!!(sport && year && rating?.stateAbbr)}
            href={`/db/rankings/industry-player/${sportQuery}/${year}/?state=${rating?.stateAbbr?.toLowerCase()}`}
          >
            <Text className={styles.ranking} color="primary">
              {rating?.consensusStateRank || '-'}
            </Text>
          </ConditionalLink>
        </div>
      </div>
      <div className={styles.statusContainer}>
        {(hasStatus || hasPredictions) && (
          <StatusContainer
            isPlayerCard
            player={player}
            rpm={rpm}
            sport={rating?.sport}
            status={status}
          />
        )}

        {!status?.transferredAsset?.assetUrl && !hasPredictions && (
          <UndecidedStatus />
        )}
      </div>
    </div>
  );
};
